import { useContext, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { shallowEqual } from 'react-redux';

import { Box, Button, Page, TextButton } from '@zillow/constellation-10';
import {
  EnterprisePageFeature,
  RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit,
  useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties,
  useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim,
} from '@zg-rentals/rental-manager-env';
import { parseQueryString } from '@zg-rentals/string-utils';

import AuthCheckWrapper from '../AuthCheckWrapper';
import { PageHeading } from './components/PageHeading';
import { Filters } from './components/Filters';
import { PropertyTable } from './components/PropertyTable';
import AddFeedListingsModal from '../../components/Modal/AddFeedListingsModal';
import AddPropertyModal from '../../components/Modal/AddPropertyModal';
import ClaimPropertiesModal from '../../components/Modal/ClaimPropertiesModal';
import NotificationBanner from '../../components/NotificationBanner';
import PostClaimingModal from '../../components/Modal/PostClaimingModal';
import WizardUserOnboarding from '../../components/WizardUserOnboarding';
import EmailVerification from '../PropertiesPage/components/EmailVerification';
import ClaimPathBanners from './components/ClaimPathBanners';
import Notifications from '../PropertiesPage/components/Notifcations';

import QueryConstants from '../../constants/QueryConstants';
import PropertiesConstants from '../../constants/PropertiesConstants';
import { CLAIM_YOUR_LISTING_COOKIES } from '../../constants/CookieConstants';

import {
  closeBanner,
  initializeFiltersFromUrl,
  removeQueryParam,
  renderInitialDisplayModal,
  updateUrlWithFilters,
} from '../../utils/propertiesPageUtils';
import accessUtils from '../../utils/accessUtils';
import contextUtils from '../../utils/contextUtils';

import PropertiesPageCSEvents from '../../model/ClickstreamEvent/PropertiesPageCSEvents';
import AddPropertyModalCSEvents from '../../model/ClickstreamEvent/AddPropertyModalCSEvents';

import { useSelector } from '../../selectors/useSelector';
import { AppSelectors } from '../../selectors';

import { useGetPropertiesData } from '../../hooks/queries/zram';

import { RMWContext } from '../../providers/RMWContext';

import type PropertiesPageConstants from '../PropertiesPage/PropertiesPageConstants';
import type { AccountViewListingsDto } from '@zg-rentals/ts-rental-manager-api-client';

export type FilterState = {
  searchText: string;
  subNavFilterType: string;
};

const defaultFilters = {
  searchText: PropertiesConstants.PROPERTIES_SEARCHTEXT_DEFAULT,
  subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
};

export const ZRAMPropertiesPage = () => {
  const location = useLocation();
  const history = useHistory();

  const { paths } = useContext(RMWContext);

  const { user, cookieEnabledServices } = useSelector(
    (state) => ({
      user: state.user,
      cookieEnabledServices: state.app.cookieEnabledServices,
    }),
    shallowEqual,
  );
  const isScreenMobile = useSelector((state) => AppSelectors.makeIsScreenMobile(state));

  const isCheckInitialDisplayModal = useRef(true);

  const [filters, setFilters] = useState<FilterState>(initializeFiltersFromUrl(location, defaultFilters));
  const [isValidatingFilters, setIsValidatingFilters] = useState(true);
  const [showAddFeedListingsModal, setShowAddFeedListingsModal] = useState(false);
  const [showClaimPropertiesModal, setShowClaimPropertiesModal] = useState({
    isOpen: false,
    isClaimMoreModal: false,
  });
  const [showPostClaimingModal, setShowPostClaimingModal] = useState(false);
  const [showAddPropertyModal, setShowAddPropertyModal] = useState(false);

  const [showPaymentsNotification, setDisplayPaymentsNotification] = useState(false);
  const [showApplicationsNotification, setDisplayApplicationsNotification] = useState(false);
  const [showLeasesNotification, setDisplayLeasesNotification] = useState(false);
  const [showListingClaimCompleteBanner, setDisplayListingClaimCompleteBanner] = useState(false);
  const isNonAdvertiser = accessUtils.isNonAdvertiser({ user });

  const {
    data: propertiesData,
    isPending: isPropertiesPending,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useGetPropertiesData(filters);

  const {
    total: numTotalListings = 0,
    freeClaimedListingCount: numClaimedListings = 0,
    archived: numArchivedListings = 0,
    freeBonusListingCount: numBonusListings = 0,
    atRisk: numAtRiskListings = 0,
  } = propertiesData?.listingCount || {};

  const { data: { newPropertiesCount = 0, hasNewProperties: hasNewPropertiesToClaim = false } = {} } =
    useRentalManagerUserClaimableListingsWebApiHandler_userHasNewPropertiesToClaim({
      enabled: user.features.canClaimFreeListings,
    });
  const { data: { unitProperties: claimableProperties = [] } = {}, isFetching: isClaimablePropertiesFetching } =
    useRentalManagerUserClaimableListingsWebApiHandler_getClaimableProperties(
      {},
      {
        enabled: user.features.canClaimFreeListings,
      },
    );

  const canAccessOnboardingWizard = accessUtils.canAccessOnboardingWizard({
    cookieEnabledServices,
    user,
  });

  const [isViewingWizardUserOnboarding, setIsViewingWizardUserOnboarding] = useState(canAccessOnboardingWizard);

  const canAccessClaimPathModal = accessUtils.canAccessClaimPath({
    isScreenMobile,
    hasAnyPropertiesToClaim: claimableProperties.length > 0,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_MODAL,
    isNonAdvertiser,
  });

  const canAccessClaimPathMorePropertiesModal = accessUtils.canAccessClaimMorePath({
    isScreenMobile,
    hasAnyClaimedListings: numClaimedListings > 0,
    hasNewPropertiesToClaim,
    cookie: CLAIM_YOUR_LISTING_COOKIES.HAS_CLOSED_CLAIM_MORE_MODAL,
  });

  const { search } = location;
  const queryParams = parseQueryString(search);

  const isArchivedView = filters.subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED;

  useEffect(() => {
    // Scroll to top of page on initial load and when switching between views
    window.scrollTo(0, 0);

    // Clear search text when switching to archived view
    if (isArchivedView) {
      setFilters({ ...filters, searchText: '' });
    }
  }, [isArchivedView]);

  useEffect(() => {
    const isFromMarketingPaymentsRoute = contextUtils.isFromMarketingPaymentsRoute(search);
    const isFromMarketingLeasesRoute = contextUtils.isFromMarketingLeasesRoute(search);
    const isFromMarketingApplicationsRoute = contextUtils.isFromMarketingApplicationsRoute(search);

    if (user.restrictions.isAllowed) {
      if (isFromMarketingPaymentsRoute) {
        setDisplayPaymentsNotification(true);
      } else if (isFromMarketingLeasesRoute) {
        setDisplayLeasesNotification(true);
      } else if (isFromMarketingApplicationsRoute && user.applicationsEnabled) {
        setDisplayApplicationsNotification(true);
      }
    }

    PropertiesPageCSEvents.ViewPropertiesPage({ user });
  }, []);

  useEffect(() => {
    // Once data has loaded, validate if user can access the requested tab
    if (!isPropertiesPending && !isClaimablePropertiesFetching) {
      const bypassOlympusRedirect = queryParams.bypassOlympusRedirect === 'true';
      if (isNonAdvertiser && numTotalListings === 0 && !bypassOlympusRedirect) {
        window.location.href = `${paths.adManagementPage}/${paths.adManagementNonAdvertiserOlympus}`;
      }

      const validatedFilters = validateFiltersAccess(initializeFiltersFromUrl(location, filters));
      setFilters(validatedFilters);

      if (validatedFilters.subNavFilterType !== filters.subNavFilterType) {
        updateUrlWithFilters(validatedFilters, location, history);
      }

      setIsValidatingFilters(false);
    }
  }, [location.search, isPropertiesPending, isClaimablePropertiesFetching]);

  useEffect(() => {
    if (!queryParams.subNavFilterType) {
      return;
    }
    if (user.customers.length && !user.impersonatorEmail && !user.impersonatorUserToken) {
      let pageViewType;
      switch (filters.subNavFilterType) {
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ON_MARKET:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_FOR_RENT;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.OFF_MARKET:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_OFF_MARKET;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_AT_RISK;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_BONUS;
          break;
        case PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED:
          pageViewType = EnterprisePageFeature.MY_PROPERTIES_CLAIMED;
          break;
      }
      if (pageViewType) {
        RentalManagerEnterpriseEventTrackingWebApiHandler_trackPageVisit({ page: pageViewType }).catch(() => {});
      }
    }
  }, [filters.subNavFilterType, search]);

  // Open initial modal based on query params
  useEffect(() => {
    if (isCheckInitialDisplayModal.current && !isPropertiesPending) {
      isCheckInitialDisplayModal.current = false;

      checkInitialDisplayModal();
    }
  }, [isPropertiesPending]);

  // Open modal when query param added via left nav
  useEffect(() => {
    checkInitialDisplayModal();
  }, [queryParams[QueryConstants.POSTING_PATH], queryParams[QueryConstants.ADD_A_FEED]]);

  const updateFilters = (newFilters: FilterState) => {
    setFilters(newFilters);
    updateUrlWithFilters(newFilters, location, history);
  };

  const validateFiltersAccess = (filters: FilterState): FilterState => {
    const { subNavFilterType } = filters;
    const canAccessClaimed =
      numClaimedListings > 0 ||
      accessUtils.canAccessClaimPath({
        isScreenMobile,
        hasAnyPropertiesToClaim: claimableProperties.length > 0,
        isNonAdvertiser: accessUtils.isNonAdvertiser({ user }),
      });

    if (
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.CLAIMED && !canAccessClaimed) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.AT_RISK && numAtRiskListings === 0) ||
      (subNavFilterType === PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.FREE_BONUS_LISTINGS &&
        numBonusListings === 0) ||
      !Object.values(PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE).includes(subNavFilterType)
    ) {
      return {
        ...filters,
        subNavFilterType: PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ALL,
      };
    }
    return filters;
  };

  const checkInitialDisplayModal = () => {
    renderInitialDisplayModal({
      search,
      paths,
      totalListingsWithoutFilters: numTotalListings,
      showAddPropertyModal,
      setShowAddPropertyModal: handleAddPropertyClick,
      canAccessOnboardingWizard: canAccessOnboardingWizard && isViewingWizardUserOnboarding,
      properties: propertiesData as AccountViewListingsDto,
      setShowAddFeedListingsModal: () => {
        if (user.restrictions.isAllowed && accessUtils.canAccessAddFeedListingsModal({ user })) {
          setShowAddFeedListingsModal(true);
        }
      },
      setShowPostClaimingModal,
      setShowClaimPropertiesModal,
      canAccessClaimPathModal,
      canAccessClaimPathMorePropertiesModal,
    });
  };

  const handleAddPropertyClick = ({ shouldSendAnalytics = false }: { shouldSendAnalytics?: boolean }) => {
    const shouldRedirectToPayments = contextUtils.isRedirectToPayments(search);

    if (user.features.canAddListing && user.restrictions.isAllowed) {
      setShowAddPropertyModal(true);
    }

    if (shouldSendAnalytics) {
      AddPropertyModalCSEvents.Open({
        user,
        triggerObject: shouldRedirectToPayments ? 'start_collecting_rent_button' : undefined,
      });

      AddPropertyModalCSEvents.AddPropertyClick({ user });
    }
  };

  const handleAddFeedListingsClick = () => {
    setShowAddFeedListingsModal(true);
    AddPropertyModalCSEvents.AddFeedClick({ user });
  };

  const onBannerClose = (bannerToClose: (typeof PropertiesPageConstants)[keyof typeof PropertiesPageConstants]) => {
    closeBanner(
      bannerToClose,
      setDisplayPaymentsNotification,
      setDisplayApplicationsNotification,
      setDisplayLeasesNotification,
      setDisplayListingClaimCompleteBanner,
    );
  };

  const handleViewArchivedPropertiesClick = () => {
    history.push(
      `${paths.properties}?${QueryConstants.SUB_NAV_FILTER_TYPE}=${PropertiesConstants.PROPERTIES_SUBNAV_FILTER_TYPE.ARCHIVED}`,
    );
  };

  return (
    <AuthCheckWrapper>
      <Page.Root
        data-testid="ZRAMPropertiesPage"
        data-panda-theme="zillow"
        data-eventid={PropertiesPageCSEvents.ViewPropertiesPage.data_event_id}
        data-eventdescription="View properties page"
      >
        <Page.Header css={{ padding: '0 !important', margin: '0 !important', maxWidth: 'none' }}>
          <PageHeading
            handleAddPropertyClick={handleAddPropertyClick}
            handleAddFeedListingsClick={handleAddFeedListingsClick}
            isArchivedView={isArchivedView}
          />
        </Page.Header>
        <Page.Content css={{ padding: { base: '0 {spacing.600}', xl: '0 {spacing.1200}' }, maxWidth: '100%' }}>
          {/* Banners */}
          {user.features.canAccessFeatureLaunchNotifications && <NotificationBanner />}
          <Notifications
            displayPaymentsNotification={showPaymentsNotification}
            displayApplicationsNotification={showApplicationsNotification}
            displayLeasesNotification={showLeasesNotification}
            displayListingClaimCompleteBanner={showListingClaimCompleteBanner}
            handleBannerCloseClick={onBannerClose}
          />
          <EmailVerification />
          <ClaimPathBanners hasAnyClaimedListings={numClaimedListings > 0} />
          {/* Table */}
          {isArchivedView ? null : <Filters filters={filters} setFilters={updateFilters} />}
          <PropertyTable filters={filters} setFilters={updateFilters} isSkeletonView={isValidatingFilters} />
          {/* CTAs */}
          {numArchivedListings > 0 && !isArchivedView && (
            <TextButton onClick={handleViewArchivedPropertiesClick} css={{ marginTop: '600' }}>
              View archived properties
            </TextButton>
          )}
          {hasNextPage && (
            <Box css={{ display: 'flex', justifyContent: 'center', marginTop: '600' }}>
              <Button tone="brand" emphasis="filled" onClick={() => fetchNextPage()} loading={isFetchingNextPage}>
                Load more properties
              </Button>
            </Box>
          )}
        </Page.Content>
        {/* Modals */}
        {canAccessOnboardingWizard && isViewingWizardUserOnboarding && (
          <WizardUserOnboarding onClose={() => setIsViewingWizardUserOnboarding(false)} />
        )}
        {showAddFeedListingsModal && (
          <AddFeedListingsModal
            onClose={() => {
              removeQueryParam([QueryConstants.ADD_A_FEED], location, history);
              setShowAddFeedListingsModal(false);
            }}
          />
        )}
        {showAddPropertyModal && (
          <AddPropertyModal
            isOpen={showAddPropertyModal}
            onClose={() => {
              removeQueryParam([QueryConstants.POSTING_PATH, QueryConstants.REDIRECT_TO], location, history);
              setShowAddPropertyModal(false);
            }}
          />
        )}
        {!isScreenMobile && showClaimPropertiesModal.isOpen && (
          <ClaimPropertiesModal
            onClose={() => {
              setShowClaimPropertiesModal({ ...showClaimPropertiesModal, isOpen: false });
            }}
            isClaimMoreModal={showClaimPropertiesModal.isClaimMoreModal}
            newPropertiesCount={newPropertiesCount}
          />
        )}
        {showPostClaimingModal && (
          <PostClaimingModal
            onClose={() => {
              removeQueryParam([QueryConstants.CLAIM_DASHBOARD], location, history);
              setShowPostClaimingModal(false);
            }}
          />
        )}
      </Page.Root>
    </AuthCheckWrapper>
  );
};
